/**
 * Every number that decides how the game feels, in one file.
 *
 * Spec §4's tuning warning is about the scoring axes, but the same discipline
 * applies here: Phase 1's whole gate is "is it fun to just go down", and that
 * lives entirely in these constants.
 */
export const TUNE = {
  /** Fixed simulation rate. Spec §7: non-negotiable, 60Hz, decoupled from render. */
  TICK_HZ: 60,
  /** Never simulate more than this many ticks in one frame — avoids spiral of death. */
  MAX_TICKS_PER_FRAME: 5,

  /**
   * Slightly above real gravity. Ski games that use 9.81 feel soggy because
   * the player has no upper body, no terrain read through their feet, and a
   * 3-inch screen. This is the smallest lie that makes it read.
   */
  GRAVITY: 11.3,

  // -- Steering ------------------------------------------------------------
  /** Peak yaw rate at full edge, rad/s, before the speed curve. */
/**
   * Carve-first steering (the "realistic physics" rebuild).
   *
   * The old law was rate = TURN_RATE x authority(speed) with authority
   * shrinking as speed rose. Measured end-to-end it produced 0.45 rad/s at
   * EVERY speed — a 44m turn radius at 20 m/s, in a game that is now about
   * threading trees. Real skis work the other way around: the edge sets a
   * RADIUS, so the turn rate grows with speed, until the grip budget caps it
   * — and past that cap the ski drifts, which the velocity-chases-skis
   * alignment already models. Radius from edge, rate from speed, cap from
   * grip: that is carving.
   */
  /** Full-edge carve radius at a standstill-ish walk, metres. */
  CARVE_RADIUS_LOW: 5.0,
  /** Full-edge carve radius at CARVE_RADIUS_SPEED and above, metres. */
  CARVE_RADIUS_HIGH: 11.0,
  CARVE_RADIUS_SPEED: 25,
  /**
   * Lateral acceleration budget, m/s^2 (~2.7g). Above it the carve
   * saturates and demanded curvature becomes drift instead. Generous by
   * real-world standards — a World Cup turn peaks near 3g — because "easy to
   * make turns" outranks strict realism here.
   */
  CARVE_MAX_LAT_ACCEL: 26,
  /**
   * Turning you keep at a standstill — the walk-the-skis-around pivot that
   * cures the stopped-against-a-tree soft-lock. Fades out by
   * PIVOT_FADE_SPEED so it never touches the racing feel.
   */
  PIVOT_TURN_RATE: 0.6,
  PIVOT_FADE_SPEED: 3.5,
  /** Heading authority while airborne, rad/s at full deflection. */
  AIR_TURN_RATE: 1.3,
  /** How fast the edge angle follows the thumb. Higher = twitchier. */
  EDGE_RESPONSE: 13.0,
  /** Steering authority retained while airborne. */
  AIR_STEER: 0.42,

  // -- Grip: the velocity chases the skis ----------------------------------
  /**
   * Base rate, 1/s, at which lateral drift converges back onto the ski's
   * direction. This is the whole grip model now: bounded by construction, no
   * clamped spring, no windup, no spiral. Snow scales it — powder bites fast,
   * ice floats — so surfaces differ in *character*, never in whether the
   * controls work.
   */
  ALIGN_BASE: 5.0,
  /** Alignment floor for a flat ski — released thumb still tracks. */
  ALIGN_EDGE_FLOOR: 0.55,
  /** Checking converges drift much harder — that is what a hard check is. */
  ALIGN_BRAKE_BOOST: 1.8,
  /**
   * Surface term of the *brake* scrub, per unit of the snow's skidDrag.
   * Carving never slows you down — alignment is a pure rotation, and no tax
   * of any kind applies without the brake held. A braked slash in powder
   * (skidDrag 2.1) throws a wall of snow and dies; on ice (0.18) it is a
   * long glassy drift. If you are slowing down, it is because you asked to.
   */
  SLIP_TAX: 0.08,
  /** Flat brake scrub on the drift the check converges — the slarve's bite. */
  BRAKE_SLIP_TAX: 0.6,
  /** Lateral speed that reads as full effort on the grip/work gauge, m/s. */
  GRIP_FULL_SKID: 8.0,
  /** Drift beyond this is a sketchy moment (display state — never a crash). */
  DRIFT_SKETCH: 8.5,
  /** Spatial frequency of the wind-slab grip lottery, cycles/m. */
  GRIP_NOISE_SCALE: 0.055,

  // -- Assists -------------------------------------------------------------
  /** Released-thumb fall-line seek: proportional gain, 1/s. */
  AUTO_CENTER_GAIN: 1.3,
  /** Cap on the assist's yaw rate, rad/s. Trims a line; cannot ski one. */
  AUTO_CENTER_MAX: 0.5,
  /** Low-pass on the assist so terrain-cell normal steps don't twitch it. */
  AUTO_CENTER_LAG: 4.0,
  /** Below this horizontal-normal magnitude (~7°) the assist stays out. */
  AUTO_CENTER_MIN_SLOPE: 0.12,
  /** Seconds after leaving the ground during which a pop still fires. */
  COYOTE_TIME: 0.1,
  /** Seconds a mistimed pop stays buffered to fire on touchdown. */
  POP_BUFFER: 0.15,

  // -- Speed ---------------------------------------------------------------
  /** Drag multiplier while tucking. Spec §8: hold = less drag. */
  TUCK_DRAG_SCALE: 0.55,
  /**
   * Plough multiplier while tucking. Tuck previously trimmed only air drag,
   * but on soft snow plough is the larger force — so tucking downhill in
   * powder visibly did nothing, which read as the game ignoring the input.
   */
  TUCK_PLOUGH_SCALE: 0.7,
  /** Fraction of plough resistance shed at full planing speed. */
  PLANE_REDUCE: 0.45,
  /** Speed at which planing is fully developed, m/s. */
  PLANE_SPEED: 24,
  /**
   * Steering authority retained while tucking. Was 0.45, which made going
   * fast a commitment to going straight; fast should stay flyable.
   */
  TUCK_STEER_SCALE: 0.85,
  /** Deceleration from a full check/slarve, m/s². */
  BRAKE_DECEL: 13.0,
  /** Steering authority while checking; you can redirect hard on a slarve. */
  BRAKE_STEER_SCALE: 1.45,

  // -- Air -----------------------------------------------------------------
  /** Upward impulse from a pop off a lip, m/s. */
  POP_IMPULSE: 5.4,
  /** Minimum seconds between pops. */
  POP_COOLDOWN: 0.35,
  /** Quadratic drag while airborne, per m. */
  AIR_DRAG: 0.0021,
  /** Clearance above the surface before we call the skier airborne. */
  AIRBORNE_EPS: 0.06,

  // -- Landing -------------------------------------------------------------
  /**
   * Speed scrubbed as a fraction of how badly the landing is mismatched.
   * severity = sin(angle between velocity and the slope plane).
   * A matched landing on a steep face keeps nearly everything; a flat landing
   * at speed keeps almost nothing. Spec §7 — this is what makes drop *choice*
   * a decision rather than a button.
   */
  LAND_SCRUB: 0.95,
  /** Floor on retained speed so a bad landing never fully stops you dead. */
  LAND_MIN_RETAIN: 0.35,
  /** Normal-direction impact speed above which the landing gets sketchy, m/s. */
  LAND_SKETCH_IMPACT: 5.5,
  /**
   * Normal-direction impact speed above which you crash, m/s. Arcade-wide
   * band: with speed conserved through carves, the par solver arrived at
   * every drop hot, and at stricter thresholds it braked to a crawl before
   * its own cliff bands rather than risk the landing — 17 seconds of tiptoe
   * on the green. Hard landings scrub speed and read as SKETCH; only
   * genuinely hucking to flat ends the run. Sending it must be the fast line.
   */
  LAND_CRASH_IMPACT: 15.5,

  // -- Compressions --------------------------------------------------------
  /** Speed lost per m/s of normal velocity absorbed while grounded. */
  COMPRESSION_SCRUB: 0.10,

  // -- Crash ---------------------------------------------------------------
  /** Seconds of lost momentum. Spec §4: ~1.5s, no hard fail, you always finish. */
  CRASH_TIME: 1.5,
  /** Fraction of speed kept through a crash. Tried 0.3 — the par solver
   * immediately found a line where crashing was worth it, which is exactly the
   * lesson a time trial must not teach. Crashes are avoidable now (no spinout,
   * generous landing thresholds); they can afford to stay expensive. */
  CRASH_SPEED_KEEP: 0.22,
  /** Seconds the sketchy-recovery state reads for. */
  SKETCH_TIME: 0.7,

  // -- Sluff (spec §6) -----------------------------------------------------
  /** Velocity-proportional drag applied when fully caught, 1/s. */
  SLUFF_DRAG: 2.2,
  /** Load above which being caught reads as a sketchy moment. */
  SLUFF_SKETCH: 0.45,
  /** Load above which you are buried — a crash, and recoverable. */
  SLUFF_BURY: 0.86,
  /** Metres behind the skier that released snow starts from. */
  SLUFF_SPAWN_BACK: 4.5,
  /**
   * How much one nearby particle loads the skier.
   *
   * The load is a sum over every particle within reach, so this sets how many
   * of them it takes to bury you. At 0.5 a dozen particles saturated it
   * instantly — a skier straightlining a 42° powder face, which should outrun
   * its own release comfortably, was buried before it got moving and never
   * escaped. Around 0.11 it takes being properly engulfed.
   */
  SLUFF_LOAD_PER_PARTICLE: 0.11,
  /** How fast sluff accelerates downhill relative to gravity along the slope. */
  SLUFF_GRAVITY: 0.72,
  /** Sluff's own drag — it is snow, not a boulder. */
  SLUFF_DRAG_SELF: 0.42,
  /** Radius, metres, within which a sluff particle loads the skier. */
  SLUFF_REACH: 4.5,

  // -- Obstacles -----------------------------------------------------------
  /** Speed kept after clipping a tree or rock. */
  IMPACT_SPEED_KEEP: 0.35,
  /** Impact speed above which hitting an obstacle crashes you, m/s. */
  IMPACT_CRASH_SPEED: 9.0,
}

export type Tuning = typeof TUNE
